import React, { useState, useEffect } from 'react';
import { 
  ChatBubbleLeftRightIcon,
  PlusIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  PencilIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { API_CONFIG, API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { getAccessToken } from '../../utils/authStorage';
import { Button } from '@/components/ui/button';

interface Message {
  id: string;
  subject: string;
  content: string;
  status: string;
  priority: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  category: {
    id: string;
    name: string;
  };
  responses?: MessageResponse[];
  created_at: string;
  updated_at: string;
}

interface MessageResponse {
  id: string;
  author: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
  content: string;
  resolved: boolean;
  is_internal: boolean;
  created_at: string;
}

interface Category {
  id: string | number;
  name: string;
  description: string;
}

const SupportMessagesPage: React.FC = () => {
  const { user } = useAuth();
  const isSuperuser = user?.isSuperuser || false;
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResolvedModal, setShowResolvedModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    category_id: '',
    subject: '',
    content: '',
    priority: 'normal'
  });
  const [responseData, setResponseData] = useState({
    content: '',
    resolved: false
  });
  const [editData, setEditData] = useState({
    subject: '',
    content: '',
    priority: 'normal'
  });

  useEffect(() => {
    loadMessages();
    loadCategories();
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showCreateModal]);

  const loadCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError('');
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_CATEGORIES}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const list = data.categories || [];
        setCategories(list);
      } else {
        setCategoriesError('Erro ao carregar categorias. Tente novamente.');
        await response.json().catch(() => ({}));
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      setCategoriesError('Erro ao carregar categorias. Tente novamente.');
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      setError('');
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_MESSAGES}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      } else {
        setError('Erro ao carregar mensagens');
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolvida':
        return 'bg-green-600 text-white border-green-600 dark:bg-green-600 dark:text-white';
      case 'em_analise':
        return 'bg-amber-600 text-white border-amber-600 dark:bg-amber-600 dark:text-white';
      case 'aberta':
        return 'bg-red-600 text-white border-red-600 dark:bg-red-600 dark:text-white';
      case 'respondida':
        return 'bg-blue-600 text-white border-blue-600 dark:bg-blue-600 dark:text-white';
      case 'fechada':
        return 'bg-gray-600 text-white border-gray-600 dark:bg-gray-600 dark:text-white';
      default:
        return 'bg-muted text-foreground border-border';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'aberta': 'Aberta',
      'em_analise': 'Em Análise',
      'respondida': 'Respondida',
      'resolvida': 'Resolvida',
      'fechada': 'Fechada',
    };
    return labels[status] || status;
  };

  const loadMessageDetail = async (messageId: string) => {
    try {
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_MESSAGE_DETAIL}${messageId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        const message = data.data;
        setSelectedMessage(message);
        window.dispatchEvent(new Event('support-notifications-updated'));
        
        // Se a mensagem estiver resolvida, mostrar modal de ticket resolvido
        if (message.status === 'resolvida' || message.status === 'fechada') {
          setShowResolvedModal(true);
        } else {
          setShowResponseModal(true);
        }
      } else {
        setError('Erro ao carregar detalhes da mensagem');
      }
    } catch (err) {
      console.error('Error loading message detail:', err);
      setError('Erro ao carregar detalhes da mensagem');
    }
  };

  const handleSendResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMessage) return;

    // Verificar se a mensagem está resolvida
    if (selectedMessage.status === 'resolvida' || selectedMessage.status === 'fechada') {
      setShowResponseModal(false);
      setShowResolvedModal(true);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_MESSAGE_RESPOND}${selectedMessage.id}/respond/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: responseData.content.trim(),
          resolved: responseData.resolved,
        }),
      });

      const responseData_result = await response.json();

      if (response.ok) {
        setSuccess('Resposta enviada com sucesso!');
        setShowResponseModal(false);
        setResponseData({ content: '', resolved: false });
        setSelectedMessage(null);
        loadMessages();
        window.dispatchEvent(new Event('support-notifications-updated'));
      } else {
        setError(responseData_result.error || responseData_result.detail || 'Erro ao enviar resposta');
      }
    } catch (err) {
      console.error('Error sending response:', err);
      setError('Erro ao enviar resposta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMessage) return;

    setSubmitting(true);
    setError('');

    try {
      const token = getAccessToken();
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_MESSAGE_UPDATE}${selectedMessage.id}/update/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subject: editData.subject.trim(),
          content: editData.content.trim(),
          priority: editData.priority,
        }),
      });

      const responseData_result = await response.json();

      if (response.ok) {
        setSuccess('Mensagem atualizada com sucesso!');
        setShowEditModal(false);
        setSelectedMessage(null);
        loadMessages();
      } else {
        setError(responseData_result.error || responseData_result.detail || 'Erro ao atualizar mensagem');
      }
    } catch (err) {
      console.error('Error updating message:', err);
      setError('Erro ao atualizar mensagem');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = getAccessToken();
      // Validar campos antes de enviar
      if (!formData.category_id) {
        setError('Por favor, selecione uma categoria');
        setSubmitting(false);
        return;
      }

      const trimmedSubject = formData.subject.trim();
      const trimmedContent = formData.content.trim();

      if (trimmedSubject.length < 5) {
        setError('O assunto deve ter pelo menos 5 caracteres');
        setSubmitting(false);
        return;
      }

      if (trimmedContent.length < 10) {
        setError('A mensagem deve ter pelo menos 10 caracteres');
        setSubmitting(false);
        return;
      }

      // Verificar se category_id existe nas categorias carregadas
      const selectedCategory = categories.find(cat => String(cat.id) === String(formData.category_id));
      if (!selectedCategory && categories.length > 0) {
        console.error('Categoria selecionada não encontrada:', formData.category_id);
        console.error('Categorias disponíveis:', categories);
        setError('Categoria inválida. Por favor, selecione uma categoria válida.');
        setSubmitting(false);
        return;
      }

      if (categories.length === 0) {
        setError('Categorias não carregadas. Por favor, recarregue a página.');
        setSubmitting(false);
        return;
      }

      const requestBody = {
        category_id: formData.category_id,
        subject: trimmedSubject,
        content: trimmedContent,
        priority: formData.priority,
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.SUPPORT_MESSAGES_CREATE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      if (response.ok) {
        setSuccess('Mensagem criada com sucesso!');
        setShowCreateModal(false);
        setFormData({
          category_id: '',
          subject: '',
          content: '',
          priority: 'normal'
        });
        loadMessages();
      } else {
        // Tratar diferentes formatos de erro
        let errorMessage = 'Erro ao criar mensagem';
        if (responseData.error) {
          errorMessage = responseData.error;
        } else if (responseData.detail) {
          errorMessage = responseData.detail;
        } else if (responseData.details) {
          // Se houver erros de validação detalhados
          const details = responseData.details;
          if (typeof details === 'object') {
            const detailMessages = Object.entries(details).map(([field, messages]) => {
              const msgArray = Array.isArray(messages) ? messages : [messages];
              return `${field}: ${msgArray.join(', ')}`;
            });
            errorMessage = detailMessages.join('; ');
          } else {
            errorMessage = String(details);
          }
        } else if (responseData.message) {
          errorMessage = responseData.message;
        }
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error creating message:', err);
      setError('Erro ao criar mensagem. Verifique sua conexão.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-foreground">Carregando mensagens...</div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
      <div className="bg-card rounded-lg shadow-lg p-6 md:p-8 lg:p-10 w-full border border-border">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <ChatBubbleLeftRightIcon className="w-8 h-8 text-secondary" />
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                Central de Mensagens
              </h1>
            </div>
            <p className="text-muted-foreground">
              Visualize e gerencie suas mensagens de suporte
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            variant="hero"
            className="whitespace-nowrap flex-shrink-0"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Nova Mensagem</span>
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border-2 border-red-600 rounded-lg flex items-start gap-3 shadow-lg">
            <ExclamationCircleIcon className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-200 font-medium text-base">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-200 transition-colors"
              aria-label="Fechar mensagem de erro"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-900/30 border-2 border-green-600 rounded-lg flex items-start gap-3 shadow-lg">
            <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-200 font-medium text-base">{success}</p>
            </div>
            <button
              onClick={() => setSuccess('')}
              className="text-green-400 hover:text-green-200 transition-colors"
              aria-label="Fechar mensagem de sucesso"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Messages List */}
        {messages.length === 0 ? (
          <div className="bg-card rounded-lg border border-border p-12 text-center">
            <ChatBubbleLeftRightIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-foreground text-lg mb-2">
              Nenhuma mensagem encontrada
            </p>
            <p className="text-muted-foreground text-sm">
              Clique em "Nova Mensagem" para criar sua primeira mensagem de suporte
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                onClick={() => loadMessageDetail(message.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    loadMessageDetail(message.id);
                  }
                }}
                className="bg-card rounded-lg border border-border p-6 hover:border-secondary hover:bg-secondary/5 transition-all duration-300 hover:shadow-lg cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {message.subject}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mb-2">
                      <span>{message.category?.name || 'Sem categoria'}</span>
                      {isSuperuser && (
                        <span className="text-muted-foreground">
                          • {message.user?.email || 'Usuário desconhecido'}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(message.status)}`}>
                    {getStatusLabel(message.status)}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                  {message.content}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      Criada em: {new Date(message.created_at).toLocaleDateString('pt-BR')}
                    </span>
                    {message.responses && message.responses.length > 0 && (
                      <span className="text-secondary">
                        • {message.responses.length} resposta{message.responses.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isSuperuser && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          loadMessageDetail(message.id);
                        }}
                        className="px-3 py-1 text-xs bg-transparent hover:bg-brand-accent/10 border border-brand-accent text-foreground rounded-lg transition-colors"
                      >
                        Ver Detalhes
                      </button>
                    )}
                    {!isSuperuser && String(message.user?.id) === String(user?.id) && message.status === 'aberta' && (!message.responses || message.responses.length === 0) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditData({
                            subject: message.subject,
                            content: message.content,
                            priority: message.priority
                          });
                          setSelectedMessage(message);
                          setShowEditModal(true);
                        }}
                        className="px-3 py-1 text-xs bg-card hover:bg-secondary/5 border border-border text-muted-foreground hover:text-foreground rounded-lg transition-colors flex items-center gap-1"
                      >
                        <PencilIcon className="w-3 h-3" />
                        Editar
                      </button>
                    )}
                    {isSuperuser && (
                      message.status === 'resolvida' || message.status === 'fechada' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMessage(message);
                            setShowResolvedModal(true);
                          }}
                          className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-1"
                        >
                          Ticket Fechado
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMessage(message);
                            setResponseData({ content: '', resolved: false });
                            setShowResponseModal(true);
                          }}
                          className="px-3 py-1 text-xs bg-transparent hover:bg-brand-accent/10 border border-brand-accent text-foreground rounded-lg transition-colors flex items-center gap-1"
                        >
                          <PaperAirplaneIcon className="w-3 h-3" />
                          Responder
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Message Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-2xl border-2 border-secondary transform transition-all duration-200 scale-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                Nova Mensagem de Suporte
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setFormData({
                    category_id: '',
                    subject: '',
                    content: '',
                    priority: 'normal'
                  });
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleCreateMessage} className="space-y-4 [&_input[type='text']]:!bg-background [&_textarea]:!bg-background [&_select]:!bg-background">
              <div>
                <label htmlFor="category_id" className="block text-sm font-medium text-muted-foreground mb-1">
                  Categoria *
                </label>
                <select
                  id="category_id"
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  required
                  disabled={categoriesLoading || !!categoriesError || (!categoriesLoading && !categoriesError && categories.length === 0)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {categoriesLoading ? (
                    <option value="" disabled>Carregando categorias...</option>
                  ) : categoriesError ? (
                    <option value="" disabled>Erro ao carregar categorias</option>
                  ) : !categoriesLoading && !categoriesError && categories.length === 0 ? (
                    <option value="" disabled>Nenhuma categoria disponível</option>
                  ) : (
                    <>
                      <option value="">Selecione uma categoria</option>
                      {categories.map((category) => (
                        <option key={String(category.id)} value={String(category.id)}>
                          {category.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                {categoriesError && (
                  <p className="text-xs text-red-500 mt-1">{categoriesError}</p>
                )}
                {!categoriesLoading && !categoriesError && categories.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Nenhuma categoria disponível. Recarregue a página ou entre em contato com o suporte.
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-muted-foreground mb-1">
                  Assunto *
                </label>
                <input
                  type="text"
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  minLength={5}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary"
                  placeholder="Descreva brevemente o assunto"
                />
              </div>

              <div>
                <label htmlFor="priority" className="block text-sm font-medium text-muted-foreground mb-1">
                  Prioridade
                </label>
                <select
                  id="priority"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary"
                >
                  <option value="baixa">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div>
                <label htmlFor="content" className="block text-sm font-medium text-muted-foreground mb-1">
                  Mensagem *
                </label>
                <textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={6}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary resize-none"
                  placeholder="Descreva sua solicitação ou problema..."
                />
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({
                      category_id: '',
                      subject: '',
                      content: '',
                      priority: 'normal'
                    });
                    setError('');
                  }}
                  className="px-4 py-2 bg-card hover:bg-secondary/5 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="hero"
                  disabled={submitting}
                >
                  {submitting ? 'Enviando...' : 'Enviar Mensagem'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Response Modal */}
      {showResponseModal && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-3xl border-2 border-secondary transform transition-all duration-200 scale-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                Responder Mensagem
              </h2>
              <button
                onClick={() => {
                  setShowResponseModal(false);
                  setSelectedMessage(null);
                  setResponseData({ content: '', resolved: false });
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Message Details */}
            <div className="bg-card rounded-lg p-4 mb-4 border border-border">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-foreground">{selectedMessage.subject}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    De: {selectedMessage.user?.email || 'Usuário desconhecido'}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(selectedMessage.status)}`}>
                  {getStatusLabel(selectedMessage.status)}
                </span>
              </div>
              <p className="text-muted-foreground text-sm mt-2">{selectedMessage.content}</p>
              
              {/* Previous Responses */}
              {selectedMessage.responses && selectedMessage.responses.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h4 className="text-sm font-semibold text-foreground mb-2">Respostas anteriores:</h4>
                  <div className="space-y-3">
                    {selectedMessage.responses.map((resp) => (
                      <div key={resp.id} className="bg-muted rounded p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-muted-foreground">
                            {resp.author?.first_name} {resp.author?.last_name} ({resp.author?.email})
                          </span>
                          {resp.resolved && (
                            <span className="text-xs px-2 py-0.5 bg-green-900/30 text-green-400 rounded">
                              Resolvido
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-foreground">{resp.content}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(resp.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSendResponse} className="space-y-4">
              <div>
                <label htmlFor="response_content" className="block text-sm font-medium text-muted-foreground mb-1">
                  Resposta *
                </label>
                <textarea
                  id="response_content"
                  value={responseData.content}
                  onChange={(e) => setResponseData({ ...responseData, content: e.target.value })}
                  required
                  rows={6}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary resize-none"
                  placeholder="Digite sua resposta..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="resolved"
                  checked={responseData.resolved}
                  onChange={(e) => setResponseData({ ...responseData, resolved: e.target.checked })}
                  className="w-4 h-4 text-secondary bg-background border-border rounded focus:ring-secondary"
                />
                <label htmlFor="resolved" className="text-sm text-muted-foreground">
                  Marcar como resolvido
                </label>
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowResponseModal(false);
                    setSelectedMessage(null);
                    setResponseData({ content: '', resolved: false });
                    setError('');
                  }}
                  className="px-4 py-2 bg-card hover:bg-secondary/5 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="hero"
                  disabled={submitting}
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  {submitting ? 'Enviando...' : 'Enviar Resposta'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Message Modal */}
      {showEditModal && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-2xl border-2 border-secondary transform transition-all duration-200 scale-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">
                Editar Mensagem
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedMessage(null);
                  setEditData({ subject: '', content: '', priority: 'normal' });
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Fechar modal"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateMessage} className="space-y-4 [&_input[type='text']]:!bg-background [&_textarea]:!bg-background [&_select]:!bg-background">
              <div>
                <label htmlFor="edit_subject" className="block text-sm font-medium text-muted-foreground mb-1">
                  Assunto *
                </label>
                <input
                  type="text"
                  id="edit_subject"
                  value={editData.subject}
                  onChange={(e) => setEditData({ ...editData, subject: e.target.value })}
                  required
                  minLength={5}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary"
                />
              </div>

              <div>
                <label htmlFor="edit_priority" className="block text-sm font-medium text-muted-foreground mb-1">
                  Prioridade
                </label>
                <select
                  id="edit_priority"
                  value={editData.priority}
                  onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary"
                >
                  <option value="baixa">Baixa</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              <div>
                <label htmlFor="edit_content" className="block text-sm font-medium text-muted-foreground mb-1">
                  Mensagem *
                </label>
                <textarea
                  id="edit_content"
                  value={editData.content}
                  onChange={(e) => setEditData({ ...editData, content: e.target.value })}
                  required
                  rows={6}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-secondary resize-none"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-900/30 border border-red-600 rounded-lg">
                  <p className="text-red-200 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedMessage(null);
                    setEditData({ subject: '', content: '', priority: 'normal' });
                    setError('');
                  }}
                  className="px-4 py-2 bg-card hover:bg-secondary/5 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancelar
                </button>
                <Button
                  type="submit"
                  variant="hero"
                  disabled={submitting}
                >
                  {submitting ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resolved Ticket Modal */}
      {showResolvedModal && selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-2xl p-8 w-full max-w-md border-2 border-border transform transition-all duration-200 scale-100">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 p-3 bg-gray-900/30 rounded-full">
                <CheckCircleIcon className="w-16 h-16 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Ticket Fechado
              </h3>
              <p className="text-muted-foreground mb-6">
                Este ticket está concluído. Se deseja abrir um novo ticket, clique no botão abaixo.
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => {
                    setShowResolvedModal(false);
                    setSelectedMessage(null);
                  }}
                  className="flex-1 px-4 py-2 bg-card hover:bg-secondary/5 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                >
                  Fechar
                </button>
                <Button
                  onClick={() => {
                    setShowResolvedModal(false);
                    setSelectedMessage(null);
                    setShowCreateModal(true);
                  }}
                  variant="hero"
                  className="flex-1"
                >
                  <PlusIcon className="w-4 h-4" />
                  Nova
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportMessagesPage;

